import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search,
  Command,
  Bell,
  Moon,
  Sun,
  ChevronDown,
  Activity,
  Check,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useExecution } from "@/store/executionStore";
import { useAuth } from "@/auth/AuthProvider";
import { rehydrateOrgData } from "@/lib/orgs";
import { UserMenu } from "./UserMenu";

function HiveLogo() {
  return (
    <div className="flex items-center gap-2">
      <div className="relative grid h-7 w-7 place-items-center">
        <svg viewBox="0 0 32 32" className="h-7 w-7">
          <defs>
            <linearGradient id="hg" x1="0" y1="0" x2="32" y2="32">
              <stop stopColor="#FFB627" />
              <stop offset="1" stopColor="#E89611" />
            </linearGradient>
          </defs>
          <path d="M16 2 L27.7 8.5 V21.5 L16 28 L4.3 21.5 V8.5 Z" fill="url(#hg)" />
          <path d="M16 9 L21 12 V18 L16 21 L11 18 V12 Z" className="fill-black/70 dark:fill-black/80" />
        </svg>
      </div>
      <span className="text-[15px] font-semibold tracking-tight text-app">Hive</span>
      <span className="hidden rounded-md bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-app-2 sm:inline">
        Momentum AI
      </span>
    </div>
  );
}

const ORG_COLOR = "#f59e0b";

function OrgSelector() {
  const { me, switchOrg } = useAuth();
  const pushToast = useHive((s) => s.pushToast);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const active = me.orgs.find((o) => o.id === me.activeOrgId) ?? me.orgs[0];
  if (!active) return null;

  const choose = async (id: string) => {
    setOpen(false);
    if (id === active.id || busy) return;
    setBusy(true);
    try {
      await switchOrg(id);
      await rehydrateOrgData();
    } catch {
      pushToast({ title: "Switch failed", body: "Couldn't switch organization — retry", tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/60 px-2.5 py-1.5 text-sm transition hover:border-app-strong disabled:opacity-60"
      >
        <span className="grid h-5 w-5 place-items-center rounded text-xs font-semibold" style={{ background: `${ORG_COLOR}22`, color: ORG_COLOR }}>
          {active.name[0]?.toUpperCase()}
        </span>
        <span className="font-medium text-app">{active.name}</span>
        <ChevronDown size={13} className="text-app-2" />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              className="absolute left-0 top-11 z-50 w-60 rounded-xl border border-app bg-surface p-1.5 shadow-window"
            >
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-app-2">
                Organizations
              </div>
              {me.orgs.map((o) => (
                <button
                  key={o.id}
                  onClick={() => void choose(o.id)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-surface-2"
                >
                  <span className="grid h-6 w-6 place-items-center rounded-md text-xs font-semibold" style={{ background: `${ORG_COLOR}22`, color: ORG_COLOR }}>
                    {o.name[0]?.toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-app">{o.name}</span>
                    <span className="block text-[10px] capitalize text-app-2">{o.role}</span>
                  </span>
                  {o.id === active.id && <Check size={14} className="accent" />}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export function SystemBar() {
  const { theme, toggleTheme, setPalette, setActivity, setNotif, notifications } =
    useHive();
  const unread = notifications.filter((n) => !n.read).length;
  const running = useExecution((s) => s.executions.filter((e) => e.status === "running").length);

  return (
    <div className="absolute inset-x-0 top-0 z-50 h-[52px]">
      <div className="glass-strong flex h-full items-center gap-3 border-b border-app px-4">
        <HiveLogo />
        <div className="mx-1 h-5 w-px bg-app/60" />
        <OrgSelector />

        {/* Global search */}
        <button
          onClick={() => setPalette(true)}
          className="group ml-2 flex h-9 max-w-md flex-1 items-center gap-2 rounded-xl border border-app bg-surface-2/50 px-3 text-sm text-app-2 transition hover:border-app-strong"
        >
          <Search size={15} />
          <span className="flex-1 text-left">Search apps, integrations, hooks…</span>
          <kbd className="flex items-center gap-0.5 rounded-md border border-app bg-surface px-1.5 py-0.5 text-[10px] text-app-2">
            <Command size={10} /> K
          </kbd>
        </button>

        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => setPalette(true)}
            className="grid h-9 w-9 place-items-center rounded-xl text-app-1 transition hover:bg-surface-2"
            title="Command palette"
          >
            <Command size={16} />
          </button>

          {/* Active agents */}
          <button
            onClick={() => setActivity(true)}
            className="flex h-9 items-center gap-2 rounded-xl border border-app bg-surface-2/50 px-2.5 text-xs font-medium text-app-1 transition hover:border-app-strong"
            title="AI Activity Center"
          >
            <span className="relative flex h-2 w-2">
              {running > 0 && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" />
              )}
              <span className={`relative inline-flex h-2 w-2 rounded-full ${running > 0 ? "bg-amber-400" : "bg-app/30"}`} />
            </span>
            <Activity size={14} />
            <span className="tabular-nums">{running}</span>
          </button>

          {/* Notifications */}
          <button
            onClick={() => setNotif(true)}
            className="relative grid h-9 w-9 place-items-center rounded-xl text-app-1 transition hover:bg-surface-2"
            title="Notifications"
          >
            <Bell size={16} />
            {unread > 0 && (
              <span className="absolute right-1.5 top-1.5 grid h-3.5 w-3.5 place-items-center rounded-full bg-rose-500 text-[8px] font-bold text-white">
                {unread}
              </span>
            )}
          </button>

          {/* Theme */}
          <button
            onClick={toggleTheme}
            className="grid h-9 w-9 place-items-center rounded-xl text-app-1 transition hover:bg-surface-2"
            title="Toggle theme"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
              </motion.span>
            </AnimatePresence>
          </button>

          {/* Profile / account menu */}
          <UserMenu />
        </div>
      </div>
    </div>
  );
}
